/**
 * Axis bounds for the charts: close enough to the data to show its shape,
 * rounded enough to label, and never opening a side of zero the data never
 * reaches — a balance that stays positive gets no negative half.
 */

/** The nearest 1 / 2 / 5 × 10ⁿ at or above `raw`, so ticks land on round numbers. */
function niceStep(raw) {
  const size = Math.abs(raw) || 1
  const base = 10 ** Math.floor(Math.log10(size))
  const steps = [1, 2, 2.5, 5, 10]
  return base * steps.find((s) => size <= s * base + Number.EPSILON)
}

/**
 * @param {number[]} values
 * @param {object} [o]
 * @param {number} [o.gridlines=4]  how many gaps to aim for — the coarser the
 *                                 step, the more empty band the rounding adds
 * @returns {{ min: number, max: number, step: number }}
 */
export function niceScale(values, { gridlines = 4 } = {}) {
  const finite = values.filter((v) => Number.isFinite(v))
  if (!finite.length) return { min: 0, max: 1, step: 1 }

  let low = Math.min(...finite)
  let high = Math.max(...finite)
  if (low === high) {
    // A flat series still needs a band to sit in, and 0 still needs to read as 0.
    const pad = Math.abs(low) * 0.1 || 1
    low = low > 0 ? Math.max(low - pad, 0) : low - pad
    high = high < 0 ? Math.min(high + pad, 0) : high + pad
  }

  const step = niceStep((high - low) / Math.max(gridlines, 1))
  let min = Math.floor(low / step) * step
  let max = Math.ceil(high / step) * step
  if (Math.min(...finite) >= 0) min = Math.max(min, 0)
  // Strictly negative, not merely "not positive": a series of nothing but zero
  // would otherwise collapse to no band at all.
  if (Math.max(...finite) < 0) max = Math.min(max, 0)
  return { min, max, step }
}
